import { WidgetAppearanceSettings } from '@chilipiper/api-client/src/chat-platform-builder'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import * as v from 'valibot'

export type AppearanceSettings = {
  actionColor: string
  /** 0-32 */
  bottomSpacing: number
  dataFieldsVisiblity: { [dataFieldId: string]: boolean }
  launcherPosition: 'left' | 'right'
  /** 0-32 */
  sideSpacing: number
  themeMode: 'light' | 'dark'
  customTriggers: {
    drafts: CustomTrigger[]
    published: CustomTrigger[]
  }
  companyName?: string
  consentFooter: ConsentFooterContent
}

type ConsentLink = {
  text: string
  url: string
}

type ConsentFooterContent = {
  text: string
  links?: ConsentLink[]
}

type CustomTrigger = {
  id: string
  elementId: string
  journeyId: string
  updatedAt: string
}

export type SettingsData = { settings: AppearanceSettings; version?: string; workspaceId: string }

const getAppearanceSettingsKey = (tenantId: string, workspaceId: string) => [
  'appearance-settings',
  tenantId,
  workspaceId,
]
const getAppearanceSettingsMutationKey = (tenantId: string, workspaceId: string) => [
  ...getAppearanceSettingsKey(tenantId, workspaceId),
  'mutation',
]

const useSettingQuery = (tenantId: string, workspaceId: string) => {
  const { chatPlatformChat } = useApiClient()
  return {
    queryKey: getAppearanceSettingsKey(tenantId, workspaceId),
    queryFn: () => {
      return chatPlatformChat
        .getApiV1PublicTenantTenantidWorkspacesWorkspaceidWidgetAppearance(tenantId, workspaceId)
        .catch(e => {
          // for some readon 422 is actually 404...
          if (e.status === 422) {
            return null
          }
          throw e
        })
    },
    retry: false,
    enabled: Boolean(tenantId && workspaceId),
  } as const
}

export const useGetAppearanceSettings = (tenantId: string, workspaceId: string) => {
  const { data, isLoading, isError } = useQuery(useSettingQuery(tenantId, workspaceId))
  const parsedData = useMemo((): SettingsData | undefined => {
    if ((!isLoading && !isError) || data) {
      return {
        settings: validateAppearanceSettings(data?.settings) ?? DEFAULT_APPEARANCE_SETTINGS,
        version: data?.version != null ? `${data.version}` : undefined,
        workspaceId: data?.workspaceId ?? workspaceId,
      }
    }
    return undefined
  }, [data, workspaceId, isError, isLoading])
  return { appearanceSettings: parsedData, isLoading, isError }
}

export const useAppearanceSettingsUpdater = (tenantId: string, workspaceId: string) => {
  const { chatPlatformBuilder } = useApiClient()
  const queryClient = useQueryClient()
  const settingsQuery = useSettingQuery(tenantId, workspaceId)

  const mutation = useMutation({
    mutationKey: getAppearanceSettingsMutationKey(tenantId, workspaceId),

    mutationFn: async ({
      appearanceSettings,
      currentVersion,
    }: {
      appearanceSettings: AppearanceSettings
      currentVersion?: string
    }) => {
      if (currentVersion) {
        return chatPlatformBuilder.putApiV1TenantTenantidWorkspacesWorkspaceidWidgetAppearance(
          tenantId,
          workspaceId,
          { settings: appearanceSettings, currentVersion: Number(currentVersion) }
        )
      } else {
        return chatPlatformBuilder.postApiV1TenantTenantidWorkspacesWorkspaceidWidgetAppearance(
          tenantId,
          workspaceId,
          { settings: appearanceSettings }
        )
      }
    },
    onMutate: async ({ appearanceSettings, currentVersion }) => {
      await queryClient.cancelQueries({ queryKey: settingsQuery.queryKey })
      const nextVersion = currentVersion ? Number(currentVersion) + 1 : 0
      queryClient.setQueryData<WidgetAppearanceSettings>(settingsQuery.queryKey, () => ({
        workspaceId,
        version: nextVersion,
        settings: appearanceSettings,
      }))
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: getAppearanceSettingsKey(tenantId, workspaceId) })
    },
  })
  return {
    updateSettings: mutation.mutate,
    isUpdating: mutation.isPending,
    hasError: mutation.isError,
  }
}

const validateAppearanceSettings = (appearanceSettings: unknown): AppearanceSettings | null => {
  try {
    return v.parse(APPEARANCE_SETTINGS_SCHEMA, appearanceSettings)
  } catch {
    return null
  }
}

const DEFAULT_APPEARANCE_SETTINGS = {
  actionColor: 'rgb(84, 61, 255)',
  sideSpacing: 24,
  bottomSpacing: 24,
  themeMode: 'light' as const,
  launcherPosition: 'right' as const,
  customTriggers: { drafts: [], published: [] } as {
    drafts: CustomTrigger[]
    published: CustomTrigger[]
  },
  dataFieldsVisiblity: {
    PersonFirstName: true,
    PersonLastName: true,
    PersonEmail: true,
    CompanyName: true,
  },
  consentFooter: {
    text: "Chili Piper provides this chat. You agree this chat may be recorded. See Chili Piper's Privacy Statement.",
    links: [
      {
        text: "Chili Piper's Privacy Statement",
        url: 'https://www.chilipiper.com/privacy-policy',
      },
    ],
  } as ConsentFooterContent,
} as const

const APPEARANCE_SETTINGS_SCHEMA = v.object({
  actionColor: v.optional(v.string(), DEFAULT_APPEARANCE_SETTINGS.actionColor),
  bottomSpacing: v.optional(v.number(), DEFAULT_APPEARANCE_SETTINGS.bottomSpacing),
  dataFieldsVisiblity: v.optional(
    v.record(v.string(), v.boolean()),
    DEFAULT_APPEARANCE_SETTINGS.dataFieldsVisiblity
  ),
  launcherPosition: v.optional(
    v.union([v.literal('left'), v.literal('right')]),
    DEFAULT_APPEARANCE_SETTINGS.launcherPosition
  ),
  sideSpacing: v.optional(v.number(), DEFAULT_APPEARANCE_SETTINGS.sideSpacing),
  themeMode: v.optional(
    v.union([v.literal('light'), v.literal('dark')]),
    DEFAULT_APPEARANCE_SETTINGS.themeMode
  ),
  customTriggers: v.optional(
    v.object({
      drafts: v.array(
        v.object({
          id: v.string(),
          elementId: v.string(),
          journeyId: v.string(),
          updatedAt: v.string(),
        })
      ),
      published: v.array(
        v.object({
          id: v.string(),
          elementId: v.string(),
          journeyId: v.string(),
          updatedAt: v.string(),
        })
      ),
    }),
    DEFAULT_APPEARANCE_SETTINGS.customTriggers
  ),
  companyName: v.optional(v.string()),
  consentFooter: v.optional(
    v.object({
      text: v.string(),
      links: v.optional(
        v.array(
          v.object({
            text: v.string(),
            url: v.string(),
          })
        )
      ),
    }),
    DEFAULT_APPEARANCE_SETTINGS.consentFooter
  ),
})
